import type { Product } from '@titularis/api-client';
import { nomeDoProduto, separarLeis } from './produtos';
import { contar, emMilhar } from './texto';

/**
 * A comparação "Grátis e Pro" da última etapa das boas-vindas.
 *
 * **Ela lê o contrato do catálogo, e não decide acesso nenhum** (G-POLICY).
 * Cada linha nasce de uma chave que o produto **concede** (`grants`), e o nome
 * vem do rótulo do servidor quando ele existe: Legislação Comentada é uma chave
 * que nasce sem passar por aqui (D-45), e sem o rótulo a tabela mostraria a
 * chave crua para quem está conhecendo a plataforma.
 *
 * **Uma linha por assunto, e não uma por chave** (10/09/2026). O Enac concede
 * nove chaves, e a tabela saía com nove linhas em que sete diziam exatamente
 * "Texto da lei / Texto e comentários disponíveis" - a pessoa lia a mesma frase
 * sete vezes para descobrir uma regra só. Pior: com dois módulos, as quatro
 * linhas de questão anunciavam a mesma cota semanal como se fossem cotas
 * separadas, e o rodapé então explicava que não eram. Agora são no máximo três
 * linhas, e cada uma diz uma coisa que as outras não dizem.
 *
 * **Quais leis, a Oferta responde.** Aqui vai a contagem: numa moldura de altura
 * fixa, sete nomes empurrariam para fora da tela justamente a comparação que a
 * etapa existe para mostrar. Quem quiser a lista clica em "Conhecer o Pro", que
 * é onde a decisão acontece.
 *
 * **Um produto de legislação não promete questão.** É o erro fácil desta tela:
 * anunciar "questões ilimitadas" num produto que só abre o comentário de uma
 * lei. Por isso as linhas de questão só saem se houver chave de módulo.
 */
interface Limites {
  freeWeeklyAnswers: number;
  freeWeeklyCommentaries: number;
}

export interface Vantagem {
  chave: string;
  rotulo: string;
  /** O que a linha cobre, quando o rótulo sozinho não diz. Nulo quando diria o óbvio. */
  detalhe?: string;
  gratis: string;
  pro: string;
}

/** As chaves que dão questão para responder e resolução para ler. */
const MODULOS = ['enac', 'objective', 'essay'];

/**
 * A cota semanal em palavras.
 *
 * **Zero e "não sei" são coisas diferentes**, e é por isso que o limite é
 * opcional aqui: com o `study/status` fora do ar, dizer "nenhuma por semana" seria
 * inventar uma regra dura que ninguém configurou. Sem o número, a célula assume
 * a ignorância; com ele em zero, ela diz o que a configuração diz.
 *
 * O substantivo fica no rótulo da linha, e não aqui: "Questões · 3 por semana" não
 * precisa repetir "questões" dentro da célula.
 */
function porSemana(quantas: number | undefined): string {
  if (quantas === undefined) return 'Limite indisponível';
  if (quantas === 0) return 'Nenhuma por semana';
  return `${emMilhar(quantas)} por semana`;
}

export function vantagensDasBoasVindas(produto: Product, limites?: Limites): Vantagem[] {
  const { leis, outros } = separarLeis(produto.grants);
  const modulos = outros.filter((chave) => MODULOS.includes(chave));
  const demais = outros.filter((chave) => !MODULOS.includes(chave));
  const linhas: Vantagem[] = [];

  if (modulos.length) {
    /*
     * Os módulos são nomeados só quando há mais de um: com um só, o nome já
     * está no título da tabela ("O que está incluído em Enac") e repeti-lo na
     * linha é ruído.
     */
    const quais =
      modulos.length > 1
        ? modulos.map((chave) => nomeDoProduto(chave, produto.rotulos[chave])).join(', ')
        : undefined;
    linhas.push(
      {
        chave: 'answers',
        rotulo: 'Questões',
        detalhe: quais,
        gratis: porSemana(limites?.freeWeeklyAnswers),
        pro: 'Sem limite semanal',
      },
      {
        chave: 'commentaries',
        rotulo: 'Resoluções comentadas',
        detalhe: quais,
        gratis: porSemana(limites?.freeWeeklyCommentaries),
        pro: 'Todas as disponíveis',
      },
    );
  }

  if (leis.length)
    linhas.push({
      chave: 'commented_law',
      rotulo: 'Legislação comentada',
      detalhe: contar(leis.length, 'lei', 'leis'),
      gratis: 'Texto integral',
      pro: 'Comentário artigo a artigo',
    });

  for (const chave of demais)
    linhas.push({
      chave,
      rotulo: nomeDoProduto(chave, produto.rotulos[chave]),
      gratis: 'Conforme as amostras disponíveis',
      pro: 'Incluído',
    });

  return linhas;
}

/**
 * A nota da cota compartilhada só tem o que explicar com mais de um módulo.
 *
 * Com um módulo, "as cotas são compartilhadas entre os módulos" fala de uma
 * divisão que a pessoa não tem como fazer.
 */
export function cotaCompartilhada(produto: Product): boolean {
  return produto.grants.filter((chave) => MODULOS.includes(chave)).length > 1;
}
